import { FilledIcon, OutlinedIcon } from '@one/icons';
import { PocIcon, PocIconButton, PocProgressSpinner, PocTooltip } from 'commons/components/components-wrapper';
import { IconFamily, Placement, PopoverOffset, PopoverPositioningStrategy } from '@one/web-components';
import { useId } from 'commons/hooks/use-id/use-id.hook';
import { CSSProperties, MouseEventHandler } from 'react';
import { LoadableIconWrapper } from './tooltiped-icon.style';

const defaultHorizontalOffset = 0;
const defaultButtonVerticalOffset = 8;
const defaultIconVerticalOffset = 16;
const defaultFamily = 'outlined';
const defaultStrategy = 'fixed';
const defaultPlacement = 'top';

export interface TooltipedIconComponentProps {
    icon: FilledIcon | OutlinedIcon;
    tooltipText: string;
    ariaLabel?: string;
    slot?: string;
    style?: CSSProperties;
    className?: string;
    anchor?: string;
    strategy?: PopoverPositioningStrategy;
    placement?: Placement;
    offset?: PopoverOffset;
    family?: IconFamily;
    hideTooltip?: boolean;
    dataTestId?: string;
    disabled?: boolean;
    isLoading?: boolean;
    onClick?: MouseEventHandler<HTMLOwcIconButtonElement>;
}

export const TooltipedIconComponent: React.FC<TooltipedIconComponentProps> = (props) => {
    const {
        icon,
        tooltipText,
        ariaLabel,
        slot,
        anchor,
        strategy = defaultStrategy,
        placement = defaultPlacement,
        family = defaultFamily,
        className = '',
        isLoading = false,
        onClick,
        style,
        dataTestId,
        offset = [defaultHorizontalOffset, onClick ? defaultButtonVerticalOffset : defaultIconVerticalOffset],
        hideTooltip = false,
        disabled = false,
    } = props;

    const generatedId = useId();
    const tooltipAnchorId = anchor ?? generatedId;

    const iconProps = {
        family,
        className,
        ...(onClick ? { icon } : { name: icon }),
        ...(onClick ? { onClick } : {}),
        ...(slot ? { slot } : {}),
        ...(style ? { style } : {}),
        ...(dataTestId ? { 'data-testid': `${dataTestId}-icon` } : {}),
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
        ...(disabled ? { disabled } : {}),
    };

    return (
        <>
            {onClick ? (
                <LoadableIconWrapper id={tooltipAnchorId}>
                    {isLoading ? (
                        <PocProgressSpinner variant="primary" size="small" />
                    ) : (
                        <PocIconButton {...iconProps} />
                    )}
                </LoadableIconWrapper>
            ) : (
                <PocIcon id={tooltipAnchorId} {...iconProps} />
            )}
            {!hideTooltip && (
                <PocTooltip
                    data-testid={dataTestId ? `${dataTestId}-tooltip` : undefined}
                    anchor={tooltipAnchorId}
                    strategy={strategy}
                    placement={placement}
                    offset={offset}
                >
                    {tooltipText}
                </PocTooltip>
            )}
        </>
    );
};
